import { useMemo, useState } from 'react';
import { GLOBAL_SEARCH_MIN_TERM_LENGTH } from '@sib/api';
import type { GlobalSearchBucketDto, GlobalSearchItemDto } from '@sib/api';
import { useTranslation } from '@sib/i18n';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@sib/ui';
import { LoaderCircle } from '@sib/ui/icons';
import { GlobalSearchModuleSection } from './GlobalSearchModuleSection';
import { GLOBAL_SEARCH_MODULE_LIST_CLASS_NAME } from '../GlobalSearch.constants';
import {
  getGlobalSearchTotalResults,
  getNonEmptyGlobalSearchBuckets,
  GLOBAL_SEARCH_ALL_TAB,
  resolveGlobalSearchActiveTab,
  useGlobalSearchModuleLabels,
} from '../GlobalSearch.utils';

interface GlobalSearchResultsProps {
  buckets?: GlobalSearchBucketDto[];
  searchTerm: string;
  isLoading: boolean;
  isError: boolean;
  showMinLengthHint: boolean;
  onItemSelect: (item: GlobalSearchItemDto) => void;
}

export const GlobalSearchResults = ({
  buckets,
  searchTerm,
  isLoading,
  isError,
  showMinLengthHint,
  onItemSelect,
}: GlobalSearchResultsProps) => {
  const { t } = useTranslation();
  const { all: allModulesLabel, getModuleLabel } =
    useGlobalSearchModuleLabels();
  const [activeTab, setActiveTab] = useState(GLOBAL_SEARCH_ALL_TAB);

  const nonEmptyBuckets = useMemo(
    () => getNonEmptyGlobalSearchBuckets(buckets),
    [buckets]
  );
  const totalResults = useMemo(
    () => getGlobalSearchTotalResults(nonEmptyBuckets),
    [nonEmptyBuckets]
  );
  const resolvedActiveTab = resolveGlobalSearchActiveTab(
    activeTab,
    nonEmptyBuckets
  );

  if (showMinLengthHint)
    return (
      <p className="text-primary px-4 py-6 text-center text-sm leading-6 font-medium tracking-normal">
        {t('components.topbar.search.minLength', {
          count: GLOBAL_SEARCH_MIN_TERM_LENGTH,
        })}
      </p>
    );

  if (isLoading)
    return (
      <div className="text-primary flex items-center justify-center gap-2 px-4 py-6 text-center text-sm leading-6 font-medium tracking-normal">
        <LoaderCircle className="size-4 animate-spin" />
        {t('components.topbar.search.loading')}
      </div>
    );

  if (isError)
    return (
      <p className="text-destructive px-4 py-6 text-center text-sm leading-6 font-medium">
        {t('components.topbar.search.error')}
      </p>
    );

  if (!nonEmptyBuckets.length)
    return (
      <p className="global-search-empty-message px-4 py-6">
        {t('components.topbar.search.emptyForPhrase', { searchTerm })}
      </p>
    );

  return (
    <Tabs
      value={resolvedActiveTab}
      onValueChange={setActiveTab}
      className="w-full"
    >
      <div className="border-border-soft overflow-x-auto border-b px-4 py-2">
        <TabsList className="h-auto w-max min-w-full justify-start gap-1 bg-transparent p-0">
          <TabsTrigger
            value={GLOBAL_SEARCH_ALL_TAB}
            className="global-search-tab-trigger data-[state=active]:shadow-none"
          >
            {t('components.topbar.search.moduleTab', {
              module: allModulesLabel,
              count: totalResults,
            })}
          </TabsTrigger>
          {nonEmptyBuckets.map(({ module, totalItems }) => (
            <TabsTrigger
              key={module}
              value={module}
              className="global-search-tab-trigger data-[state=active]:shadow-none"
            >
              {t('components.topbar.search.moduleTab', {
                module: getModuleLabel(module),
                count: totalItems,
              })}
            </TabsTrigger>
          ))}
        </TabsList>
      </div>

      <TabsContent value={GLOBAL_SEARCH_ALL_TAB} className="mt-0">
        {resolvedActiveTab === GLOBAL_SEARCH_ALL_TAB ? (
          <div className="custom-scrollbar-lg flex max-h-96 flex-col gap-3 overflow-y-auto py-3">
            {nonEmptyBuckets.map(({ module, totalItems }) => (
              <GlobalSearchModuleSection
                key={module}
                module={module}
                searchTerm={searchTerm}
                totalItems={totalItems}
                listClassName={GLOBAL_SEARCH_MODULE_LIST_CLASS_NAME.compact}
                onItemSelect={onItemSelect}
              />
            ))}
          </div>
        ) : null}
      </TabsContent>

      {nonEmptyBuckets.map(({ module, totalItems }) => (
        <TabsContent key={module} value={module} className="mt-0">
          {resolvedActiveTab === module ? (
            <div className="py-3">
              <GlobalSearchModuleSection
                module={module}
                searchTerm={searchTerm}
                totalItems={totalItems}
                listClassName={GLOBAL_SEARCH_MODULE_LIST_CLASS_NAME.expanded}
                onItemSelect={onItemSelect}
              />
            </div>
          ) : null}
        </TabsContent>
      ))}
    </Tabs>
  );
};
